import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

interface UseCohortMessageIdOptions {
    /**
     * Initial messageId from location.state (e.g., when creating from existing cohort)
     */
    initialCohortMessageId?: string | null;
}

/**
 * Custom hook to manage messageId state with URL synchronization using nuqs.
 *
 * This hook handles messageId from two sources:
 * 1. URL query parameter (?messageId=xxx)
 * 2. Navigation state (location.state.initialCohort.message_id)
 *
 * The URL parameter takes precedence, but if the initial cohort has a message_id
 * and there's no URL parameter, it will be set to the URL.
 *
 * @example
 * ```tsx
 * const { messageId, setMessageId, hasMessageId } = useCohortMessageId({
 *     initialCohortMessageId: location.state?.initialCohort?.message_id,
 * });
 * ```
 */
export function useCohortMessageId(options: UseCohortMessageIdOptions = {}) {
    const { initialCohortMessageId } = options;
    const location = useLocation();
    const hasInitialized = useRef(false);

    // Use nuqs for URL-synced state management
    // parseAsString returns null for missing values (no default needed)
    const [messageId, setMessageId] = useQueryState(
        'messageId',
        parseAsString.withOptions({
            history: 'replace', // Use replace to avoid polluting browser history
            shallow: false, // Ensure router is aware of changes
        })
    );

    // Handle initial cohort messageId from navigation state
    // Only set once on mount if URL doesn't already have messageId
    useEffect(() => {
        if (hasInitialized.current) return;

        const stateMessageId = initialCohortMessageId || location.state?.initialCohort?.message_id;

        if (stateMessageId && !messageId) {
            setMessageId(stateMessageId);
        }

        hasInitialized.current = true;
    }, [initialCohortMessageId, location.state?.initialCohort?.message_id, messageId, setMessageId]);

    return {
        /**
         * Current messageId value (null if not set)
         */
        messageId,
        /**
         * Set the messageId (updates URL automatically)
         */
        setMessageId,
        /**
         * Convenience boolean to check if messageId exists
         */
        hasMessageId: !!messageId,
        /**
         * Clear the messageId from URL
         */
        clearMessageId: () => setMessageId(null),
    };
}
